import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  Input,
  IconButton,
  Text,
  Flex,
  Spacer,
  Button,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Spinner,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from '../../../utils/reduxHooks';
import {
  getCartsAction,
  deleteCartItemAction,
  updateCartItemAction,
} from '../../../modules/cart/actions';
import { selectAllCarts, selectIsLoadingCarts } from '../../../modules/cart/selectors';
import { CartDTO, CartItemDTO } from '../../../modules/cart/types';
import useCustomToast from '../../components/UI/toast';
import { ButtonCustom } from '../../components/UI';
import { EditIcon, DeleteIcon } from '@chakra-ui/icons';

interface CartProps {
  onProceedToCheckout: (selectedItems: CartDTO['order_items']) => void;
}

export const Cart: React.FC<CartProps> = ({ onProceedToCheckout }) => {
  const dispatch = useAppDispatch();
  const toast = useCustomToast();
  const allCarts = useAppSelector(selectAllCarts) as CartDTO | null;
  const isListLoading = useAppSelector(selectIsLoadingCarts);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [tempAmounts, setTempAmounts] = useState<{ [key: string]: number | null }>({});
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<CartItemDTO | null>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    dispatch(getCartsAction.request());
  }, [dispatch]);

  useEffect(() => {
    if (allCarts?.order_items) {
      const initialAmounts = Object.fromEntries(
        allCarts.order_items.map(item => [item.pk, item.amount]),
      );
      setTempAmounts(initialAmounts);
    }
  }, [allCarts]);

  const handleQuantityChange = (itemPk: string, value: string) => {
    setTempAmounts(prev => ({ ...prev, [itemPk]: value === '' ? null : parseInt(value) || 1 }));
  };

  const handleEditModalOpen = (item: CartItemDTO) => {
    setEditItem(item);
    setEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setEditItem(null);
  };

  const handleSaveEdit = () => {
    if (editItem) {
      const { pk, ...rest } = editItem;
      console.log(allCarts, ' allCarts');

      dispatch(updateCartItemAction.request(pk, rest));
      toast('Товар успешно обновлен', 'success');
      handleEditModalClose();
    }
  };

  const renderCartItemRow = (item: CartDTO['order_items'][0]) => (
    <Tr key={item.pk}>
      <Td width="50px">
        <Checkbox
          isChecked={selectedProducts.includes(item.pk)}
          onChange={() =>
            setSelectedProducts(prev =>
              prev.includes(item.pk) ? prev.filter(id => id !== item.pk) : [...prev, item.pk],
            )
          }
        />
      </Td>
      <Td>{new Date(allCarts?.created_at || '').toLocaleDateString()}</Td>
      <Td>{item.article_number}</Td>
      <Td>{item.good_name}</Td>
      <Td>{item.comment || '—'}</Td>
      <Td>{item.delivery_term} дн.</Td>
      <Td>
        <Input
          type="number"
          value={tempAmounts[item.pk] !== null ? String(tempAmounts[item.pk]) : ''}
          min={1}
          onChange={e => handleQuantityChange(item.pk, e.target.value)}
          width="60px"
          textAlign="center"
        />
      </Td>
      <Td>{(parseFloat(item.price) * (tempAmounts[item.pk] || item.amount)).toLocaleString()} ₽</Td>
      <Td>
        <IconButton
          aria-label="Редактировать товар"
          icon={<EditIcon />}
          onClick={() => handleEditModalOpen(item)}
          size="sm"
          mr={2}
        />
        <IconButton
          aria-label="Удалить товар"
          icon={<DeleteIcon />}
          onClick={() => {
            setItemToDelete(item.pk);
            setDeleteModalOpen(true);
          }}
          size="sm"
        />
      </Td>
    </Tr>
  );

  const totalItems = allCarts?.order_items.length;

  const totalPrice = useMemo(
    () =>
      allCarts?.order_items?.reduce(
        (sum, item) => sum + (tempAmounts[item.pk] || item.amount) * parseFloat(item.price),
        0,
      ) || 0,
    [allCarts, tempAmounts],
  );

  const selectedItemsPrice = useMemo(
    () =>
      allCarts?.order_items?.reduce(
        (sum, item) =>
          selectedProducts.includes(item.pk)
            ? sum + (tempAmounts[item.pk] || item.amount) * parseFloat(item.price)
            : sum,
        0,
      ) || 0,
    [allCarts, selectedProducts, tempAmounts],
  );

  return (
    <>
      <Box>
        <Text fontSize="2xl" fontWeight="bold" mb={6}>
          Корзина
        </Text>
        {isListLoading ? (
          <Flex justifyContent="center" alignItems="center" height="200px">
            <Spinner size="xl" />
          </Flex>
        ) : allCarts?.order_items?.length ? (
          <Table>
            <Thead>
              <Tr>
                <Th />
                <Th>Добавлено</Th>
                <Th>Артикул</Th>
                <Th>Наименование</Th>
                <Th>Комментарий</Th>
                <Th>Ожид. срок</Th>
                <Th>Количество</Th>
                <Th>Стоимость</Th>
                <Th>Действия</Th>
              </Tr>
            </Thead>
            <Tbody>{allCarts?.order_items.map(renderCartItemRow)}</Tbody>
          </Table>
        ) : (
          <Text>Нет товаров в корзине</Text>
        )}
      </Box>

      {/* {selectedProducts.length > 0 && (
        <Flex w="100%" justifyContent="center" mt={6}>
          <Button
            colorScheme="blue"
            onClick={() => {
              const selectedItems = allCarts?.order_items.filter(item =>
                selectedProducts.includes(item.pk),
              );
              onProceedToCheckout(selectedItems || []);
            }}
          >
            Купить выбранное ({selectedProducts.length} шт на {selectedItemsPrice.toLocaleString()}{' '}
            ₽)
          </Button>
        </Flex>
      )} */}

      <Box
        position="fixed"
        bottom="0"
        left="0"
        right="0"
        bg="white"
        borderTop="1px solid #e2e8f0"
        py={4}
        px={8}
      >
        <Flex alignItems="center" justifyContent="space-between">
          <Text fontWeight="bold">ИТОГО</Text>
          <Text>Товаров: {totalItems} шт</Text>
          <Text>Стоимость: {totalPrice.toLocaleString()} ₽</Text>
          <ButtonCustom
            variant="withBackground"
            // colorScheme=""
            onClick={() =>
              onProceedToCheckout(
                allCarts?.order_items.map(item => ({
                  ...item,
                  amount: tempAmounts[item.pk] ?? item.amount,
                })) || [],
              )
            }
          >
            Купить всё
          </ButtonCustom>
        </Flex>
      </Box>

      {/* AlertDialog */}
      <AlertDialog
        isOpen={isDeleteModalOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeleteModalOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Удалить товар?</AlertDialogHeader>
            <AlertDialogBody>Вы уверены, что хотите удалить этот товар?</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setDeleteModalOpen(false)}>
                Отмена
              </Button>
              <Button
                colorScheme="red"
                onClick={() => {
                  dispatch(deleteCartItemAction.request([itemToDelete!]));
                  setDeleteModalOpen(false);
                }}
                ml={3}
              >
                Удалить
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} onClose={handleEditModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Редактировать</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {/* <Text>{editItem?.good_name}</Text> */}
              {/* <NumberInput
                value={editItem?.amount || ''}
                min={1}
                onChange={value =>
                  setEditItem(prev => (prev ? { ...prev, amount: parseInt(value) } : prev))
                }
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput> */}
              <Textarea
                value={editItem?.comment || ''}
                onChange={e =>
                  setEditItem(prev => (prev ? { ...prev, comment: e.target.value } : prev))
                }
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={handleEditModalClose}>Отмена</Button>
            <Button colorScheme="teal" onClick={handleSaveEdit}>
              Сохранить
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
